import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { MerchandiseOrder, PersatuanPaymentSettings, SellerPaymentSettings, UserProfile } from '../../types';
import {
  getPersatuanPaymentSettingsDB,
  DEFAULT_PERSATUAN_PAYMENT_SETTINGS,
  getSellerPaymentSettingsDB,
  DEFAULT_SELLER_PAYMENT_SETTINGS,
  getMerchandiseItemsDB
} from '../../services/db';
import { uploadReceiptToCloudinary } from '../../lib/cloudinary';
import { useToast } from '../common/Toast';
import { processUploadedQrToSvg } from '../../utils/qrProcessor';
import {
  QrCode,
  Copy,
  Check,
  Upload,
  Building2,
  Send,
  X,
  CreditCard,
  Download,
  Loader2
} from 'lucide-react';

interface DuitNowQrModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: MerchandiseOrder | null;
  seller?: UserProfile | null;
  isNewOrder?: boolean;
  onPaymentSubmitted: (updatedOrder: MerchandiseOrder) => Promise<void>;
}

export const DuitNowQrModal: React.FC<DuitNowQrModalProps> = ({
  isOpen,
  onClose,
  order,
  isNewOrder = false,
  onPaymentSubmitted
}) => {
  const { showToast } = useToast();
  const [settings, setSettings] = useState<PersatuanPaymentSettings>(DEFAULT_PERSATUAN_PAYMENT_SETTINGS);
  const [sellerSettings, setSellerSettings] = useState<SellerPaymentSettings>(DEFAULT_SELLER_PAYMENT_SETTINGS);
  const [paymentRef, setPaymentRef] = useState<string>('');
  const [receiptImage, setReceiptImage] = useState<string>('');
  const [isUploadingReceipt, setIsUploadingReceipt] = useState<boolean>(false);
  const [isCopiedAcc, setIsCopiedAcc] = useState<boolean>(false);
  const [isCopiedRef, setIsCopiedRef] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Digital 1:1 SVG QR states
  const [digitalSvgUrl, setDigitalSvgUrl] = useState<string>('');
  const [digitalSvgString, setDigitalSvgString] = useState<string>('');
  const [isProcessingSvg, setIsProcessingSvg] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && order) {
      let isCancelled = false;
      const load = async () => {
        try {
          const persatuanSettings = await getPersatuanPaymentSettingsDB().catch(() => DEFAULT_PERSATUAN_PAYMENT_SETTINGS);
          if (!isCancelled) setSettings(persatuanSettings);

          let targetSellerId = order.sellerId;
          if (!targetSellerId && order.itemId) {
            const items = await getMerchandiseItemsDB().catch(() => []);
            const found = items.find(i => i.id === order.itemId);
            if (found?.userId) {
              targetSellerId = found.userId;
            }
          }
          if (!targetSellerId) {
            targetSellerId = 'ASMM-001';
          }

          const sSettings = await getSellerPaymentSettingsDB(targetSellerId);
          if (!isCancelled) {
            setSellerSettings(sSettings);
          }
        } catch (e) {
          console.warn('Error loading payment settings for order:', e);
        }
      };
      load();

      if (order.paymentRef) {
        setPaymentRef(order.paymentRef);
      } else {
        setPaymentRef('');
      }
      if (order.paymentProofUrl) {
        setReceiptImage(order.paymentProofUrl);
      } else {
        setReceiptImage('');
      }

      return () => { isCancelled = true; };
    }
  }, [isOpen, order]);

  const effectiveBankName = sellerSettings.bankName || settings.bankName;
  const effectiveAccountNo = sellerSettings.accountNo || settings.accountNo;
  const effectiveAccountHolder = sellerSettings.accountHolder || settings.accountHolder;
  const effectiveQrImage = sellerSettings.qrImageUrl || settings.qrImageUrl || '/badges/qr-persatuan-asmm.png';

  // Auto-process QR image into pure 1:1 SVG with DuitNow Pink (#ED0080)
  useEffect(() => {
    if (!effectiveQrImage) {
      setDigitalSvgUrl('');
      setDigitalSvgString('');
      return;
    }

    const isAlreadySvg = effectiveQrImage.startsWith('data:image/svg+xml') || effectiveQrImage.trim().startsWith('<svg');
    if (isAlreadySvg) {
      setDigitalSvgUrl(effectiveQrImage);
      if (effectiveQrImage.trim().startsWith('<svg')) {
        setDigitalSvgString(effectiveQrImage);
      } else if (effectiveQrImage.startsWith('data:image/svg+xml')) {
        try {
          const decoded = decodeURIComponent(
            effectiveQrImage.replace('data:image/svg+xml;charset=utf-8,', '').replace('data:image/svg+xml,', '')
          );
          setDigitalSvgString(decoded);
        } catch (e) {}
      }
      return;
    }

    let isMounted = true;
    setIsProcessingSvg(true);

    processUploadedQrToSvg(effectiveQrImage, { colorDark: '#ED0080' })
      .then((res) => {
        if (!isMounted || !res) return;
        setDigitalSvgUrl(res.svgDataUrl);
        setDigitalSvgString(res.svgString);
      })
      .catch((err) => {
        console.warn('Auto SVG regeneration failed for merchandise QR:', err);
      })
      .finally(() => {
        if (isMounted) setIsProcessingSvg(false);
      });

    return () => {
      isMounted = false;
    };
  }, [effectiveQrImage]);

  if (!order) return null;

  const orderShortId = order.id.slice(-6).toUpperCase();
  const orderRefText = `ORD-${orderShortId}`;

  const copyToClipboard = (text: string, type: 'acc' | 'ref') => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      if (type === 'acc') {
        setIsCopiedAcc(true);
        setTimeout(() => setIsCopiedAcc(false), 2000);
      } else {
        setIsCopiedRef(true);
        setTimeout(() => setIsCopiedRef(false), 2000);
      }
      showToast('success', 'Disalin!', `${text} telah disalin ke papan keratan.`);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
      showToast('error', 'Format Tidak Sah', 'Sila muat naik fail imej (PNG, JPG, JPEG) atau PDF sahaja.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showToast('error', 'Saiz Terlalu Besar', 'Maksimum saiz imej resit ialah 10MB.');
      return;
    }

    setIsUploadingReceipt(true);
    showToast('info', 'Memuat Naik Resit...', 'Menyimpan resit ke Cloudinary...');
    try {
      const res = await uploadReceiptToCloudinary(file, order ? `order-${order.id}` : 'merch');
      setReceiptImage(res.secureUrl);
      showToast('success', 'Resit Disimpan', 'Resit berjaya dimuat naik ke Cloudinary CDN.');
    } catch (err: any) {
      console.error('Error uploading receipt to Cloudinary:', err);
      showToast('error', 'Gagal Memuat Naik', err?.message || 'Sila cuba lagi.');
    } finally {
      setIsUploadingReceipt(false);
    }
  };

  const handleSubmitProof = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!receiptImage) {
      showToast('error', 'Resit Diperlukan', 'Sila muat naik resit atau tangkap layar bukti pembayaran sebelum menghantar tempahan.');
      return;
    }

    setIsSubmitting(true);
    try {
      const updatedOrder: MerchandiseOrder = {
        ...order,
        paymentMethod: 'DuitNow QR',
        paymentStatus: 'Menunggu Pengesahan',
        paymentRef: paymentRef.trim() || undefined,
        paymentProofUrl: receiptImage || undefined,
        paidAt: new Date().toISOString()
      };

      await onPaymentSubmitted(updatedOrder);
      onClose();
    } catch {
      showToast('error', 'Ralat', 'Gagal merekodkan pembayaran. Sila cuba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to draw rounded rectangle on canvas for high-resolution QR slip
  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x + r, y);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  // Download high-resolution PNG image with 1:1 QR code & bank details at bottom
  const handleDownloadQr = () => {
    const source = digitalSvgString
      ? URL.createObjectURL(new Blob([digitalSvgString], { type: 'image/svg+xml;charset=utf-8' }))
      : (digitalSvgUrl || effectiveQrImage);

    if (!source) {
      showToast('error', 'Ralat', 'Tiada imej kod QR untuk dimuat turun.');
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const w = 1080;
      const h = 1440;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        if (digitalSvgString) URL.revokeObjectURL(source);
        return;
      }

      // 1. Pure White Background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, w, h);

      // Outer Card Border
      ctx.strokeStyle = '#E2E8F0';
      ctx.lineWidth = 4;
      drawRoundedRect(ctx, 24, 24, w - 48, h - 48, 36);
      ctx.stroke();

      // 2. Top Header: Pill Badge & Title
      drawRoundedRect(ctx, (w - 340) / 2, 48, 340, 36, 18);
      ctx.fillStyle = '#FCE7F3';
      ctx.fill();
      ctx.strokeStyle = '#FBCFE8';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#9D174D';
      ctx.textAlign = 'center';
      ctx.fillText('DUITNOW QR & PINDAHAN BANK', w / 2, 71);

      ctx.font = '900 28px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#0F172A';
      ctx.textAlign = 'center';
      ctx.fillText('BAYARAN MERCHANDISE ASMM', w / 2, 124);

      ctx.font = '600 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#64748B';
      ctx.fillText(`${order.itemName} (x${order.quantity}) • RM ${order.totalAmount.toFixed(2)}`, w / 2, 150);

      // 3. Central 1:1 QR Code Showcase
      const qrBoxSize = 710;
      const qrBoxX = (w - qrBoxSize) / 2;
      const qrBoxY = 175;

      drawRoundedRect(ctx, qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, 28);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
      ctx.strokeStyle = '#E2E8F0';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw QR strictly 1:1
      const qrPadding = 30;
      ctx.drawImage(
        img,
        qrBoxX + qrPadding,
        qrBoxY + qrPadding,
        qrBoxSize - qrPadding * 2,
        qrBoxSize - qrPadding * 2
      );

      // 4. Bottom Section: Pindahan Manual Akaun Bank Card
      const cardX = 80;
      const cardY = 920;
      const cardW = w - 160;
      const cardH = 450;

      drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 24);
      ctx.fillStyle = '#F8FAFC';
      ctx.fill();
      ctx.strokeStyle = '#E2E8F0';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Card Header
      ctx.font = '800 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#0F172A';
      ctx.textAlign = 'left';
      ctx.fillText('Pindahan Manual Akaun Bank', cardX + 36, cardY + 44);

      // Divider line
      ctx.strokeStyle = '#E2E8F0';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(cardX + 36, cardY + 62);
      ctx.lineTo(cardX + cardW - 36, cardY + 62);
      ctx.stroke();

      // Row 1: Nama Bank & Nama Pemegang Akaun
      ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#64748B';
      ctx.fillText('NAMA BANK', cardX + 36, cardY + 96);
      ctx.fillText('NAMA PEMEGANG AKAUN', cardX + cardW / 2, cardY + 96);

      ctx.font = '900 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#0F172A';
      ctx.fillText(effectiveBankName, cardX + 36, cardY + 126);
      ctx.fillText(effectiveAccountHolder, cardX + cardW / 2, cardY + 126, cardW / 2 - 40);

      // Row 2: Nombor Akaun Bank
      ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#64748B';
      ctx.fillText('NOMBOR AKAUN BANK', cardX + 36, cardY + 172);

      // Monospace Box for Account Number
      drawRoundedRect(ctx, cardX + 36, cardY + 188, cardW - 72, 74, 16);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
      ctx.strokeStyle = '#CBD5E1';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.font = '900 34px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';
      ctx.fillStyle = '#0F172A';
      ctx.textAlign = 'center';
      ctx.fillText(effectiveAccountNo, w / 2, cardY + 238);

      // Row 3: Rujukan Bayaran Box
      drawRoundedRect(ctx, cardX + 36, cardY + 280, cardW - 72, 60, 14);
      ctx.fillStyle = '#FEF3C7';
      ctx.fill();
      ctx.strokeStyle = '#FDE68A';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#92400E';
      ctx.textAlign = 'center';
      ctx.fillText(`Rujukan Pembayaran (Wajib): ${orderRefText}`, w / 2, cardY + 317);

      // Persatuan Official Watermark
      ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#94A3B8';
      ctx.textAlign = 'center';
      ctx.fillText('Persatuan Seni Silat Awang Sulung Merah Muda Kedah (PPM-007-02-28082022)', w / 2, cardY + 410);

      // Generate PNG and trigger download
      const pngUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = `Bayaran_QR_${orderRefText}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      if (digitalSvgString) {
        URL.revokeObjectURL(source);
      }

      showToast('success', 'Imej Kod QR Dimuat Turun', 'Fail imej QR berserta butiran pindahan akaun bank berjaya dimuat turun.');
    };

    img.onerror = () => {
      if (digitalSvgString) URL.revokeObjectURL(source);
      showToast('error', 'Ralat', 'Gagal memproses imej kod QR untuk dimuat turun.');
    };

    img.src = source;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Pembayaran Kod QR DuitNow"
      maxWidth="max-w-xl"
    >
      <div className="space-y-4 text-slate-800 text-xs">
        {/* Top Summary Banner */}
        <div className="bg-gradient-to-br from-red-50 to-amber-50/40 p-4 rounded-2xl border border-red-100 flex items-center justify-between gap-3">
          <div className="space-y-1">
            <span className="text-[10px] font-black uppercase tracking-wider text-asmm-red bg-white px-2 py-0.5 rounded-full border border-red-200">
              Imbas & Bayar QR Rasmi
            </span>
            <div className="font-extrabold text-slate-900 text-sm">
              {order.itemName} <span className="text-slate-500 font-medium">(x{order.quantity})</span>
            </div>
            <div className="text-[11px] text-slate-500">
              Rujukan Tempahan: <span className="font-mono font-bold text-slate-700">#{orderShortId}</span>
            </div>
          </div>

          <div className="text-right shrink-0">
            <span className="text-[10px] font-bold text-slate-500 uppercase block">Jumlah Perlu Dibayar</span>
            <span className="text-xl font-black text-asmm-red font-mono">
              RM {order.totalAmount.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Digital 1:1 SVG QR Code Presentation Box */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 text-center space-y-3.5 shadow-xs">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#ED0080]/10 text-[#ED0080] rounded-full text-[11px] font-black tracking-wide uppercase mx-auto">
            <QrCode className="w-3.5 h-3.5" />
            <span>DuitNow QR</span>
          </div>

          {/* 1:1 Aspect Ratio Display Box */}
          <div className="flex flex-col items-center justify-center">
            <div className="bg-white p-3.5 sm:p-4 rounded-2xl border-2 border-slate-900/10 shadow-md">
              <div className="w-56 h-56 sm:w-64 sm:h-64 aspect-square flex items-center justify-center relative">
                {isProcessingSvg && (
                  <div className="absolute inset-0 bg-white/95 backdrop-blur-xs flex flex-col items-center justify-center rounded-xl z-10 space-y-2 text-center p-4">
                    <Loader2 className="w-7 h-7 text-amber-500 animate-spin" />
                    <span className="text-xs font-black text-slate-800">Menjana Semula Digital SVG 1:1...</span>
                    <span className="text-[10px] text-slate-500">Mengekstrak kod QR kepada vektor tajam</span>
                  </div>
                )}

                {digitalSvgString ? (
                  <div
                    className="w-full h-full aspect-square flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:aspect-square [&>svg]:block"
                    dangerouslySetInnerHTML={{ __html: digitalSvgString }}
                  />
                ) : (digitalSvgUrl || effectiveQrImage) ? (
                  <img
                    src={digitalSvgUrl || effectiveQrImage}
                    alt="Kod QR DuitNow"
                    className="w-full h-full aspect-square object-contain mx-auto rounded-xl"
                  />
                ) : (
                  <div className="w-full h-full aspect-square rounded-xl border-2 border-dashed border-slate-200 bg-white flex flex-col items-center justify-center text-slate-700 p-4 text-center">
                    <QrCode className="w-12 h-12 mb-2 text-slate-400 opacity-80" />
                    <span className="text-xs font-black uppercase tracking-wider">DuitNow QR</span>
                    <span className="text-[11px] text-slate-600 font-bold mt-1">{effectiveBankName}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Single Download Button */}
            {(digitalSvgString || effectiveQrImage) && (
              <div className="flex items-center justify-center pt-3">
                <button
                  type="button"
                  onClick={handleDownloadQr}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-black rounded-xl transition-all shadow-md hover:shadow-lg cursor-pointer border-none"
                  title="Muat Turun Imej Kod QR & Butiran Bank"
                >
                  <Download className="w-3.5 h-3.5 text-amber-400" />
                  <span>Muat Turun Kod QR</span>
                </button>
              </div>
            )}
          </div>

          <p className="text-[11px] text-slate-600 font-medium max-w-sm mx-auto leading-relaxed">
            Buka mana-mana aplikasi perbankan (<strong>MAE, CIMB, Bank Islam, TnG eWallet, GrabPay</strong>) dan imbas kod QR di atas.
          </p>
        </div>

        {/* Bank Account Details Card with 1-Click Copy */}
        <div className="p-4 bg-white rounded-2xl border border-slate-200 space-y-2.5 shadow-xs">
          <div className="flex items-center gap-2 text-slate-900 text-xs font-black border-b border-slate-100 pb-2">
            <CreditCard className="w-4 h-4 text-slate-600" />
            <span>Pindahan Manual Akaun Bank</span>
          </div>

          <div className="flex items-center justify-between text-[11px]">
            <span className="text-slate-500 font-bold flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5 text-slate-400" />
              <span>Nama Bank:</span>
            </span>
            <span className="font-extrabold text-slate-900">{effectiveBankName}</span>
          </div>

          <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-100">
            <span className="text-slate-500 font-bold">Nombor Akaun:</span>
            <div className="flex items-center gap-2">
              <span className="font-mono font-extrabold text-slate-900 text-xs sm:text-sm">
                {effectiveAccountNo}
              </span>
              <button
                type="button"
                onClick={() => copyToClipboard(effectiveAccountNo, 'acc')}
                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[10px] font-extrabold transition-all flex items-center gap-1 cursor-pointer"
                title="Salin Nombor Akaun"
              >
                {isCopiedAcc ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                <span>{isCopiedAcc ? 'Disalin' : 'Salin'}</span>
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-100">
            <span className="text-slate-500 font-bold">Nama Akaun Penerima:</span>
            <span className="font-bold text-slate-700 text-right text-[10px] sm:text-[11px] max-w-[240px] truncate">
              {effectiveAccountHolder}
            </span>
          </div>

          <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-100 bg-amber-50/50 p-2 rounded-xl border border-amber-200/60">
            <span className="text-amber-800 font-bold">Rujukan Pembayaran (Wajib):</span>
            <div className="flex items-center gap-1.5">
              <span className="font-mono font-black text-amber-900">{orderRefText}</span>
              <button
                type="button"
                onClick={() => copyToClipboard(orderRefText, 'ref')}
                className="px-2 py-0.5 bg-amber-200/80 hover:bg-amber-300 text-amber-900 rounded-md text-[10px] font-black transition-all flex items-center gap-1 cursor-pointer"
              >
                {isCopiedRef ? <Check className="w-2.5 h-2.5 text-emerald-800" /> : <Copy className="w-2.5 h-2.5" />}
                <span>{isCopiedRef ? 'Disalin' : 'Salin'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Verification Form */}
        <form onSubmit={handleSubmitProof} className="space-y-4 pt-1 border-t border-slate-100">
          <div className="space-y-1">
            <label className="block text-slate-800 font-extrabold text-xs">
              1. No. Rujukan Transaksi Perbankan (Pilihan/Disyorkan)
            </label>
            <input
              type="text"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              placeholder="Contoh: 20261019984321 atau DuitNow Ref"
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-semibold text-slate-900 placeholder:text-slate-400"
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-slate-800 font-extrabold text-xs">
              2. Muat Naik Resit / Tangkap Layar Pembayaran QR <span className="text-asmm-red">*</span>
            </label>

            {receiptImage ? (
              <div className="relative rounded-2xl border border-emerald-200 bg-emerald-50/40 p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <img
                    src={receiptImage}
                    alt="Pratonton Resit"
                    className="w-12 h-12 rounded-xl object-cover border border-emerald-200 shadow-2xs shrink-0"
                  />
                  <div className="min-w-0">
                    <span className="text-[10px] font-black text-emerald-800 uppercase block">Resit Dilampirkan</span>
                    <span className="text-[11px] font-bold text-slate-700 truncate block">Imej bukti bayaran QR sedia dihantar</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReceiptImage('')}
                  className="p-1.5 hover:bg-emerald-100 text-slate-500 hover:text-rose-600 rounded-lg transition-colors cursor-pointer"
                  title="Padam Imej"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className={`flex flex-col items-center justify-center p-4 border-2 border-dashed border-slate-200 hover:border-asmm-red hover:bg-red-50/30 rounded-2xl transition-all bg-slate-50/60 group ${isUploadingReceipt ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}>
                <Upload className="w-6 h-6 text-slate-400 group-hover:text-asmm-red transition-colors mb-1" />
                <span className="text-xs font-extrabold text-slate-700 group-hover:text-asmm-red">
                  {isUploadingReceipt ? 'Sedang memuat naik ke Cloudinary...' : 'Klik untuk pilih atau tangkap gambar resit'}
                </span>
                <span className="text-[10px] text-slate-400 mt-0.5">Format JPG, PNG, PDF (Disimpan di Cloudinary)</span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  disabled={isUploadingReceipt}
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
            >
              {isNewOrder ? 'Batal' : 'Bayar Nanti'}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !receiptImage || isUploadingReceipt}
              className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
            >
              <Send className="w-3.5 h-3.5" />
              <span>
                {isSubmitting
                  ? 'Menyimpan Tempahan...'
                  : isNewOrder
                    ? 'Sahkan & Hantar Tempahan Bersama Resit'
                    : 'Sahkan & Hantar Bukti Bayaran'}
              </span>
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
